import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { PillButton } from "@/components/motifs";
import { useLang } from "@/context/LanguageContext";
import { useCMS } from "@/hooks/useCMS";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_cycle-power-2/artifacts/zp1guu9j_logo_new.png";

const LOGO_SIZE = { small: "h-9", medium: "h-11", large: "h-14" };

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { lang, toggle, t } = useLang();
  const { data } = useCMS();
  const settings = data.settings || {};
  const logoCls = LOGO_SIZE[data.settings?.logo_size] || LOGO_SIZE.medium;

  const NAV = [
    { label: t("Home", "Accueil"), to: "/", key: "home" },
    { label: t("Benni & Friends", "Benni et ses amis"), to: "/BenniBooks", key: "benni" },
    { label: t("Events", "Événements"), to: "/Events", key: "events" },
    { label: t("Initiatives", "Initiatives"), to: "/Initiatives", key: "initiatives" },
    ...(settings.show_blog_nav !== false ? [{ label: t("Blog", "Blogue"), to: "/Blog", key: "blog" }] : []),
    { label: t("About", "À propos"), to: "/About", key: "about" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const LangToggle = ({ className = "" }) => (
    <button
      onClick={toggle}
      className={`inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-2 text-sm font-bold hover:border-[#2CACD4] hover:text-[#2CACD4] transition-colors cursor-pointer ${className}`}
      aria-label="Switch language"
      data-testid="lang-toggle"
    >
      <Globe size={16} />
      {lang === "en" ? "FR" : "EN"}
    </button>
  );

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background/40 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center cursor-pointer" data-testid="header-logo">
            <img src={data.settings?.logo || LOGO} alt="#ShowUsYourMoves" className={`${logoCls} w-auto object-contain`} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => {
              const active = n.to === "/" ? pathname === "/" : pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.key}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                    active ? "text-[#2CACD4]" : "text-foreground/80 hover:text-[#2CACD4]"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LangToggle />
            <PillButton to="/Home#participate" variant="pink" testid="header-join-in">
              {t("Join In", "Participer")}
            </PillButton>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LangToggle />
            <button
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-foreground text-background cursor-pointer"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="px-4 py-3 rounded-2xl text-base font-semibold hover:bg-muted cursor-pointer"
                data-testid={`mobile-nav-${n.key}`}
              >
                {n.label}
              </Link>
            ))}
            <PillButton to="/Home#participate" variant="pink" className="mt-2 justify-center" testid="mobile-join-in">
              {t("Join In", "Participer")}
            </PillButton>
          </nav>
        </div>
      )}
    </header>
  );
}
